import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePayment } from '../contexts/PaymentContext';
import { useAuth } from '../contexts/AuthContext';

const PaymentButton = ({ 
  planId, 
  projectId, 
  projectName,
  buttonText = 'Subscribe Now' 
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [localError, setLocalError] = useState('');
  const navigate = useNavigate();
  const { createPaymentSession, error: paymentError } = usePayment();
  const { user } = useAuth();
  
  const handlePayment = async () => {
    setIsProcessing(true);
    setLocalError('');
    
    try {
      // Check if user is authenticated
      if (!user) {
        setLocalError('You must be logged in to make a payment. Redirecting to login...');
        setTimeout(() => navigate('/login'), 2000);
        return;
      }
      
      // Create a payment session through our backend
      const session = await createPaymentSession({
        planId,
        projectId: projectId || 'default-project',
        projectName: projectName || 'Default Project',
        userId: user.id,
        email: user.email,
        successUrl: `${window.location.origin}/payment/success`,
        cancelUrl: `${window.location.origin}/payment/cancel`
      });
      
      // Store session details in localStorage for backup verification
      if (session && session.id) {
        // Store the subscription/payment ID based on what we receive
        if (session.id.startsWith('sub_')) {
          localStorage.setItem('pendingSubscriptionId', session.id);
          localStorage.setItem('pendingSubscriptionData', JSON.stringify({
            id: session.id,
            projectId: projectId || 'default-project',
            projectName: projectName || 'Default Project',
            createdAt: new Date().toISOString()
          }));
        } else {
          localStorage.setItem('pendingPaymentId', session.id);
          localStorage.setItem('pendingPaymentData', JSON.stringify({
            id: session.id,
            projectId: projectId || 'default-project',
            projectName: projectName || 'Default Project',
            createdAt: new Date().toISOString()
          }));
        }
      }
      
      // If we have a checkout URL, redirect to it
      if (session && session.checkout_url) {
        window.location.href = session.checkout_url;
      } else if (session && session.id) {
        // Backup if no checkout URL provided
        const idParam = session.id.startsWith('sub_') ? 
          `subscription_id=${session.id}` : `payment_id=${session.id}`;
        navigate(`/payment/success?${idParam}`);
      } else {
        throw new Error('Payment session creation failed');
      }
    } catch (error) {
      setLocalError(error.message || paymentError || 'An error occurred during payment processing');
    } finally {
      setIsProcessing(false);
    }
  };
  
  // Display any errors
  const error = localError || paymentError;
  
  return (
    <div className="w-full">
      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-100 text-red-700">
          {error}
        </div>
      )}
      
      <button
        onClick={handlePayment}
        disabled={isProcessing}
        className={`w-full flex justify-center items-center py-3 px-6 rounded-full text-white font-medium
          ${isProcessing ? 'bg-gray-400 cursor-not-allowed' : 'bg-primary-600 hover:bg-primary-700'}`}
      >
        {isProcessing ? (
          <>
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Processing...
          </>
        ) : (
          buttonText
        )}
      </button>
    </div>
  );
};

export default PaymentButton; 